import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Skeleton } from "@/components/ui";
import { StatusBanner } from "@/components/marketplace";
import { useTranslation } from "@/hooks/useTranslation";
import { useAcceptInvitation, useMyInvitations } from "@/hooks/useCollaborations";
import { useOpenCampaigns } from "@/hooks/useMarketplaceCampaigns";
import { OpportunityRow } from "./components/OpportunityRow";
import { fromInvitation, fromOpenCampaign, summarise } from "./opportunityModel";

/**
 * The creator's front door.
 *
 * `myInvitations` had lived on the server with no client reading it — a brand
 * could invite someone by name and that person would never find out. This
 * screen is that query plus the open campaigns, in one list, because a creator
 * does not care which table a chance arrived through.
 *
 * Dismissal is **local to the session and says so**. There is no
 * `declineInvitation` mutation, so hiding a row here tells the brand nothing
 * and the row returns on reload. The copy is "ocultaste", never "rechazaste",
 * and the tooltip spells out both halves. Wiring it to a real decline is a
 * server change, not a UI one.
 */
export default function Opportunities() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { invitations, loading: loadingInvites } = useMyInvitations();
  const { campaigns, loading: loadingCampaigns } = useOpenCampaigns();
  const { acceptInvitation, busy } = useAcceptInvitation();

  const [hidden, setHidden] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const loading = loadingInvites || loadingCampaigns;

  const all = useMemo(() => {
    const invited = invitations
      .filter((i) => i.status === "SENT" || i.status === "VIEWED")
      .map((i) => fromInvitation(i, t))
      .filter((o): o is NonNullable<typeof o> => o !== null);

    // An invitation and an open campaign can be the same campaign. The
    // invitation wins — it carries the brand's message and the reason to open
    // the row at all.
    const invitedCampaignIds = new Set(invited.map((o) => o.campaign.id));
    const open = campaigns
      .filter((c) => !invitedCampaignIds.has(c.id))
      .map((c) => fromOpenCampaign(c, t));

    return [...invited, ...open];
    // `t` is rebuilt every render; the data is the real input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitations, campaigns]);

  const visible = all.filter((o) => !hidden.includes(o.key));
  // Recalculated from what is on screen, so dismissing a row moves both figures.
  const { total, soonest } = summarise(visible);

  const accept = async (campaignId: string) => {
    setError(null);
    try {
      const collaboration = await acceptInvitation(campaignId);
      navigate(`/colaboraciones/${collaboration.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="adv-page">
      <header className="flex flex-wrap items-start justify-between gap-6">
        <div className="min-w-0 flex-[1_1_420px]">
          <Badge variant="accent">
            {t("marketplace.opportunities.countChip", { count: visible.length })}
          </Badge>
          <h1 className="mt-3 text-balance text-page-title text-neutral-900">
            {t("marketplace.opportunities.title")}
          </h1>
          <p className="mt-3 max-w-xl text-lead text-neutral-500">
            {t("marketplace.opportunities.lead")}
          </p>
        </div>

        {/* Two live figures. Both recalculate when a row is hidden. */}
        <div className="flex flex-[0_1_auto] gap-8 rounded-card border border-neutral-200 bg-white px-5 py-[18px] shadow-card-rest">
          <div>
            <p className="adv-label">{t("marketplace.opportunities.inOffers")}</p>
            <p className="mt-1.5 whitespace-nowrap text-figure-md text-neutral-900">
              {total ?? "—"}
            </p>
          </div>
          <div>
            <p className="adv-label">{t("marketplace.opportunities.expiresSoonest")}</p>
            <p
              className={`mt-1.5 whitespace-nowrap text-figure-sm ${
                soonest?.urgent ? "text-warning-fg" : "text-neutral-900"
              }`}
            >
              {soonest?.label ?? t("marketplace.opportunities.noExpiry")}
            </p>
          </div>
        </div>
      </header>

      {error && (
        <StatusBanner tone="danger" className="mt-6">
          {error}
        </StatusBanner>
      )}

      <div className="mt-7 space-y-3">
        {loading && all.length === 0 ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="flex gap-[18px] rounded-card border border-neutral-200 bg-white px-5 py-[18px]"
            >
              <Skeleton className="size-14 rounded-avatar-square-lg" />
              <div className="flex-1">
                <Skeleton className="h-4 w-[38%] rounded-sm" />
                <Skeleton className="mt-2 h-3 w-[64%] rounded-sm" />
                <Skeleton className="mt-3 h-3 w-[88%] rounded-sm" />
                <Skeleton className="mt-1.5 h-3 w-[52%] rounded-sm" />
              </div>
              <Skeleton className="h-8 w-28 rounded-sm" />
            </div>
          ))
        ) : visible.length === 0 ? (
          <div className="rounded-card border border-neutral-200 bg-white px-5 py-16 text-center">
            <p className="text-card-title-sm text-neutral-900">
              {t("marketplace.opportunities.emptyTitle")}
            </p>
            <p className="mx-auto mt-2 max-w-md text-meta text-neutral-500">
              {t("marketplace.opportunities.emptyBody")}
            </p>
          </div>
        ) : (
          visible.map((o) => (
            <OpportunityRow
              key={o.key}
              opportunity={o}
              busy={busy}
              onAccept={() => accept(o.campaign.id)}
              onDismiss={() => setHidden((h) => [...h, o.key])}
            />
          ))
        )}
      </div>

      {/* Dashed means "not yet real" — the same signal as the upload dropzone. */}
      {hidden.length > 0 && (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-control border border-dashed border-neutral-300 px-4 py-3">
          <p className="text-fine text-neutral-500">
            {t("marketplace.opportunities.dismissed", { count: hidden.length })}
          </p>
          <button
            type="button"
            onClick={() => setHidden([])}
            className="text-fine font-semibold text-steel-600 transition-colors hover:text-steel-500"
          >
            {t("marketplace.opportunities.undo")}
          </button>
        </div>
      )}
    </div>
  );
}
